import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t, type MessageKey } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import {
  advertisingCostsCsvUrl,
  getAdvertisingCosts,
  type AdvertisingCostsGroupBy,
  type AdvertisingCostsResult,
} from '../lib/advertising-api.js';
import { fetchRegistry, portalDisplayName } from '../lib/connectors.js';
import { fetchCaseAddressIndex } from '../lib/case-address-lookup.js';
import { DomainApiError } from '../lib/api-error.js';
import { DateField } from '../components/date-field.js';

/**
 * `/naklady-inzerce` (krok O3) — matice mesic x portal x nemovitost nad
 * SCHVALENYMI rozpady faktur portalu. Zdroj je `GET /v1/finance/advertising-costs`;
 * stejne jako `/naklady` jde o agregaci, ne o strankovany seznam (LIST-CONTRACT
 * se proto neuplatnuje). Export CSV je odkaz na tentyz endpoint s `format=csv`.
 *
 * Nazvy portalu se NEHARDCODUJI (§31) — ctou se z registry, stejne jako na
 * `/portaly`. Nemovitost se zobrazi adresou zakazky (krok O6a, polozka 2) —
 * `fetchCaseAddressIndex` cte z jiz existujiciho `GET /v1/cases`, zadny novy
 * join. Kod (propertyId) zustava jen jako sekundarni text, kdyz adresa neni znama.
 */

const GROUP_BY_OPTIONS: readonly AdvertisingCostsGroupBy[] = ['portal', 'property', 'month'];

const GROUP_BY_LABEL_KEYS: Readonly<Record<AdvertisingCostsGroupBy, MessageKey>> = {
  portal: 'webAdmin.nakladyInzerce.groupByPortal',
  property: 'webAdmin.nakladyInzerce.groupByProperty',
  month: 'webAdmin.nakladyInzerce.groupByMonth',
};

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency }).format(value);
}

export function NakladyInzercePage(): React.JSX.Element {
  const [groupBy, setGroupBy] = React.useState<AdvertisingCostsGroupBy>('property');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [result, setResult] = React.useState<AdvertisingCostsResult | null>(null);
  const [portalNames, setPortalNames] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [propertyNames, setPropertyNames] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  /** Popisky portalu — tabulka se kresli az s nimi (viz nize). */
  const [portalsLoading, setPortalsLoading] = React.useState(true);
  /**
   * Krok APP2 bod 1: id nemovitosti, na ktere uz adresa DOSLA — i kdyz k nim
   * zadna neni. Drzi se mnozina, ne priznak: cekani se pak pozna UZ V RENDERU
   * a tabulka nemuze bliknout kodem nemovitosti driv, nez odpoved dojde
   * (na tomtez dojezdil pruchod celou aplikaci u nazvu portalu, KAT1l2).
   */
  const [resolvedProperties, setResolvedProperties] = React.useState<ReadonlySet<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);

  const params = React.useMemo(
    () => ({ groupBy, ...(from !== '' ? { from } : {}), ...(to !== '' ? { to } : {}) }),
    [groupBy, from, to],
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const costs = await getAdvertisingCosts(params);
        if (!cancelled) setResult(costs);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof DomainApiError ? e.message : t('webAdmin.nakladyInzerce.loadError', { locale: 'cs' }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  /*
   * Popisky (portal, nabidka) nejsou kriticke pro CISLA, ale tabulka na ne
   * CEKA: dokud registr neodpovi, nema cim portal pojmenovat a vypisovala
   * jeho strojovy klic („reality_idnes"). Pruchod celou aplikaci to 30. 10.
   * 2026 nasel na `/naklady-inzerce` jako technicky text (oprava e2e 1 kroku
   * KAT1l2) — a makler ma cist nazev portalu, ne klic rozhrani.
   */
  /**
   * Krok APP2 bod 1 — ADRESY JEN PRO NEMOVITOSTI, KTERE JSOU V TABULCE.
   *
   * Do APP2 se tu stahoval CELY seznam zakazek kancelare (`fetchCaseAddressIndex`
   * strankuje po stovkach) jen proto, aby se k `propertyId` radku nasel nazev.
   * Pri 343 zakazkach MV to byly ctyri dotazy navic, v DEMO kancelari desitky
   * a kvota (429). Id nemovitosti pritom obrazovka ZNÁ — jsou v odpovedi.
   */
  const propertyIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const cell of result?.cells ?? []) ids.add(cell.propertyId);
    if (groupBy === 'property') for (const row of result?.summary ?? []) ids.add(row.key);
    return [...ids].filter((id) => id !== '');
  }, [result, groupBy]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const portals = await fetchRegistry();
        if (!cancelled) setPortalNames(new Map(portals.map((p) => [p.portal, p.name])));
      } catch {
        /* popisky nejsou kriticke */
      }
      if (!cancelled) setPortalsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (propertyIds.length === 0) return undefined;
    let cancelled = false;
    void (async () => {
      let found: ReadonlyMap<string, string> = new Map();
      try {
        found = (await fetchCaseAddressIndex({ propertyIds })).byPropertyId;
      } catch {
        /* popisky nejsou kriticke — radek pak rekne „Nemovitost bez adresy" */
      }
      if (cancelled) return;
      setPropertyNames((prev) => new Map([...prev, ...found]));
      setResolvedProperties((prev) => new Set([...prev, ...propertyIds]));
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyIds]);

  /** Ceka obrazovka jeste na nejaky popisek? (Pocita se v renderu, viz vyse.) */
  const labelsLoading = portalsLoading || propertyIds.some((id) => !resolvedProperties.has(id));

  const portalLabel = (portal: string): string => portalDisplayName(portalNames.get(portal));

  const propertyDisplay = (propertyId: string): React.ReactNode => {
    const address = propertyNames.get(propertyId);
    // Bod 25 CO-HNED (A26): interni ID makléři nic nerekne, ukazuje se jen adresa.
    return address ?? t('webAdmin.nakladyInzerce.unknownProperty', { locale: 'cs' });
  };

  const summaryDisplay = (key: string): React.ReactNode => {
    if (groupBy === 'portal') return portalLabel(key);
    if (groupBy === 'property') return propertyDisplay(key);
    return key;
  };

  const cells = result?.cells ?? [];

  return (
    <div className="p-6" data-testid="naklady-inzerce-page">
      <h1 className="mb-1 text-xl font-bold">{t('webAdmin.nakladyInzerce.title', { locale: 'cs' })}</h1>
      <p className="mb-4 max-w-2xl text-sm text-gray-600">{t('webAdmin.nakladyInzerce.perex', { locale: 'cs' })}</p>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          {t('webAdmin.nakladyInzerce.groupBy', { locale: 'cs' })}
          <select
            aria-label={t('webAdmin.nakladyInzerce.groupByAriaLabel', { locale: 'cs' })}
            className="rh-input block"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as AdvertisingCostsGroupBy)}
            data-testid="naklady-inzerce-groupby"
          >
            {GROUP_BY_OPTIONS.map((key) => (
              <option key={key} value={key}>
                {t(GROUP_BY_LABEL_KEYS[key], { locale: 'cs' })}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t('webAdmin.nakladyInzerce.periodFrom', { locale: 'cs' })}
          <DateField
            ariaLabel={t('webAdmin.nakladyInzerce.periodFromAriaLabel', { locale: 'cs' })}
            className="rh-input block"
            value={from}
            onValueChange={setFrom}
          />
        </label>
        <label className="text-sm">
          {t('webAdmin.nakladyInzerce.periodTo', { locale: 'cs' })}
          <DateField
            ariaLabel={t('webAdmin.nakladyInzerce.periodToAriaLabel', { locale: 'cs' })}
            className="rh-input block"
            value={to}
            onValueChange={setTo}
          />
        </label>
        <a
          className="rh-btn"
          href={advertisingCostsCsvUrl(params)}
          target="_blank"
          rel="noopener"
          data-testid="naklady-inzerce-export-csv"
        >
          {t('webAdmin.nakladyInzerce.exportCsv', { locale: 'cs' })}
        </a>
      </div>

      {(loading || labelsLoading) && <p role="status">{t('webAdmin.nakladyInzerce.loading', { locale: 'cs' })}</p>}
      {error !== null && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}

      {!loading && !labelsLoading && error === null && cells.length === 0 ? (
        <EmptyState
          title={t('webAdmin.nakladyInzerce.emptyTitle', { locale: 'cs' })}
          description={t('webAdmin.nakladyInzerce.emptyDescription', { locale: 'cs' })}
          action={
            <Link to="/neprirazene-naklady" className="rh-btn" data-testid="naklady-inzerce-empty-action">
              {t('webAdmin.nakladyInzerce.emptyAction', { locale: 'cs' })}
            </Link>
          }
        />
      ) : null}

      {!loading && !labelsLoading && error === null && cells.length > 0 ? (
        <div className="flex flex-wrap items-start gap-8">
          <section>
            <h2 className="mb-2 text-sm font-semibold">{t('webAdmin.nakladyInzerce.matrixTitle', { locale: 'cs' })}</h2>
            <table className="text-sm" data-testid="naklady-inzerce-matrix">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-6 font-normal">{t('webAdmin.nakladyInzerce.colMonth', { locale: 'cs' })}</th>
                  <th className="pr-6 font-normal">{t('webAdmin.nakladyInzerce.colPortal', { locale: 'cs' })}</th>
                  <th className="pr-6 font-normal">{t('webAdmin.nakladyInzerce.colProperty', { locale: 'cs' })}</th>
                  <th className="font-normal">{t('webAdmin.nakladyInzerce.colTotal', { locale: 'cs' })}</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((cell) => (
                  <tr
                    key={`${cell.month}-${cell.portal}-${cell.propertyId}-${cell.currency}`}
                    data-testid={`naklady-inzerce-cell-${cell.month}-${cell.portal}-${cell.propertyId}`}
                  >
                    <td className="pr-6">{cell.month}</td>
                    <td className="pr-6">{portalLabel(cell.portal)}</td>
                    <td className="pr-6">{propertyDisplay(cell.propertyId)}</td>
                    <td>{formatMoney(cell.total, cell.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">{t('webAdmin.nakladyInzerce.summaryTitle', { locale: 'cs' })}</h2>
            <table className="text-sm" data-testid="naklady-inzerce-summary">
              <tbody>
                {(result?.summary ?? []).map((row) => (
                  <tr key={`${row.key}-${row.currency}`} data-testid={`naklady-inzerce-summary-${row.key}`}>
                    <td className="pr-6">{summaryDisplay(row.key)}</td>
                    <td>{formatMoney(row.total, row.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      ) : null}
    </div>
  );
}
